import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Eye,
  AlertTriangle,
  CheckCircle2,
  Image as ImageIcon,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Download,
} from 'lucide-react';
import { getScreenshots, getScreenshotImage, getScreenshotDetails } from '../services/api';
import { useScrollToEvent } from '../hooks/useScrollToEvent';
import DateRangeFilter from '../components/DateRangeFilter';
import dayjs from 'dayjs';
import { PageHeader } from '../components/ui/page-header';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { EmptyState } from '../components/ui/empty-state';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '../components/ui/modal';
import { cn } from '../lib/utils';

function ScreenshotsPage() {
  const { workerId } = useParams();
  const [screenshots, setScreenshots] = useState([]);
  useScrollToEvent(screenshots);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filterParams, setFilterParams] = useState({ startDate: null, endDate: null, hours: null });
  const itemsPerPage = 200;

  // Zoom + pan state for the details modal
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, startPanX: 0, startPanY: 0 });

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const clampZoom = (z) => Math.min(8, Math.max(0.5, z));

  const onWheelZoom = (e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setZoom((z) => clampZoom(z * delta));
  };

  const onDragStart = (e) => {
    if (zoom <= 1) return;
    setDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPanX: pan.x,
      startPanY: pan.y,
    };
  };
  const onDragMove = (e) => {
    if (!dragging) return;
    setPan({
      x: dragRef.current.startPanX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.startPanY + (e.clientY - dragRef.current.startY),
    });
  };
  const onDragEnd = () => setDragging(false);

  useEffect(() => {
    loadScreenshots();
  }, [workerId, page, filterParams]);

  const loadScreenshots = async () => {
    try {
      setLoading(true);
      const offset = (page - 1) * itemsPerPage;
      const response = await getScreenshots(
        itemsPerPage,
        offset,
        workerId ? parseInt(workerId) : null,
        filterParams.startDate,
        filterParams.endDate
      );
      setScreenshots(response.data.data);
      setTotalCount(response.data.count || response.data.data.length);
      setError(null);
    } catch (err) {
      console.error('Failed to load screenshots:', err);
      setError('Failed to load screenshots');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (params) => {
    setFilterParams(params);
    setPage(1);
  };

  const handleViewDetails = async (screenshot) => {
    try {
      const response = await getScreenshotDetails(screenshot.id);
      setSelectedScreenshot(response.data.data);
      setDialogOpen(true);
      resetZoom();
    } catch (err) {
      console.error('Failed to load screenshot details:', err);
    }
  };

  const closeDetails = () => {
    setDialogOpen(false);
    resetZoom();
  };

  const getRiskVariant = (score) => {
    if (score >= 80) return 'destructive';
    if (score >= 50) return 'warning';
    if (score >= 20) return 'secondary';
    return 'success';
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader
        title="Screenshots"
        description="Captured screenshots from monitored activities"
      />

      <DateRangeFilter onFilterChange={handleFilterChange} />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex min-h-[300px] items-center justify-center text-sm text-muted-foreground">
          Loading screenshots...
        </div>
      ) : screenshots.length === 0 ? (
        <EmptyState
          icon={ImageIcon}
          title="No screenshots"
          description="No screenshots captured yet. Screenshots will appear here as monitoring progresses."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {screenshots.map((screenshot) => (
            <Card
              key={screenshot.id}
              id={`event-${screenshot.id}`}
              className="overflow-hidden"
            >
              <img
                src={getScreenshotImage(screenshot.id)}
                alt={`Screenshot ${screenshot.id}`}
                className={cn(
                  'h-44 w-full object-cover',
                  screenshot.has_pii && 'border-[3px] border-destructive'
                )}
              />
              <CardContent className="space-y-2 p-4">
                <div className="text-xs text-muted-foreground">
                  {dayjs(screenshot.timestamp).format('MMM DD, HH:mm:ss')}
                </div>
                <div className="truncate text-sm text-foreground">
                  {screenshot.application || 'Unknown App'}
                </div>
                <div className="flex flex-wrap gap-1">
                  {screenshot.has_pii && (
                    <Badge variant="warning">
                      <AlertTriangle className="h-3 w-3" />
                      PII
                    </Badge>
                  )}
                  {screenshot.risk_score > 0 && (
                    <Badge variant={getRiskVariant(screenshot.risk_score)}>
                      Risk: {screenshot.risk_score}
                    </Badge>
                  )}
                  {!screenshot.has_pii && screenshot.risk_score === 0 && (
                    <Badge variant="success">
                      <CheckCircle2 className="h-3 w-3" />
                      Safe
                    </Badge>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => handleViewDetails(screenshot)}
                >
                  <Eye />
                  View
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <Modal
        open={dialogOpen}
        onClose={closeDetails}
        className="max-w-[min(96vw,1600px)]"
      >
        <ModalHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <ModalTitle>Screenshot Details</ModalTitle>
            {selectedScreenshot && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => setZoom((z) => clampZoom(z / 1.25))}
                  title="Zoom out"
                >
                  <ZoomOut />
                </Button>
                <span className="min-w-[3.5rem] text-center text-xs tabular-nums text-muted-foreground">
                  {Math.round(zoom * 100)}%
                </span>
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => setZoom((z) => clampZoom(z * 1.25))}
                  title="Zoom in"
                >
                  <ZoomIn />
                </Button>
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={resetZoom}
                  title="Reset zoom"
                >
                  <Maximize2 />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  asChild={false}
                  onClick={() => window.open(getScreenshotImage(selectedScreenshot.id), '_blank')}
                  title="Open original in new tab"
                >
                  <Download />
                  Original
                </Button>
              </div>
            )}
          </div>
        </ModalHeader>
        <ModalBody className="space-y-4">
          {selectedScreenshot && (
            <>
              <div
                className={cn(
                  'relative overflow-hidden rounded-lg border border-border bg-black/80 select-none',
                  zoom > 1 ? (dragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-zoom-in'
                )}
                style={{ height: 'min(72vh, 760px)' }}
                onWheel={onWheelZoom}
                onMouseDown={onDragStart}
                onMouseMove={onDragMove}
                onMouseUp={onDragEnd}
                onMouseLeave={onDragEnd}
                onDoubleClick={() =>
                  setZoom((z) => clampZoom(z === 1 ? 2 : 1)) || (zoom !== 1 && resetZoom())
                }
              >
                <img
                  src={getScreenshotImage(selectedScreenshot.id)}
                  alt="Screenshot"
                  draggable={false}
                  className="absolute left-1/2 top-1/2 max-h-none max-w-none object-contain transition-transform duration-100"
                  style={{
                    transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: 'center',
                    height: '100%',
                    width: 'auto',
                  }}
                />
                <div className="pointer-events-none absolute bottom-2 right-2 rounded-md bg-black/60 px-2 py-1 text-[11px] text-white/80">
                  Scroll to zoom · Drag to pan · Double-click to toggle
                </div>
              </div>

              {selectedScreenshot.has_pii && (
                <Alert variant="destructive">
                  <AlertTriangle />
                  <AlertDescription>
                    <strong>PII Detected!</strong> Sensitive information highlighted with red boxes.
                    PII types: {selectedScreenshot.pii_types}
                  </AlertDescription>
                </Alert>
              )}

              <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Timestamp</dt>
                  <dd className="text-foreground">
                    {dayjs(selectedScreenshot.timestamp).format('MMMM DD, YYYY HH:mm:ss')}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Application</dt>
                  <dd className="text-foreground">{selectedScreenshot.application || '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Window</dt>
                  <dd className="truncate text-foreground" title={selectedScreenshot.active_window}>
                    {selectedScreenshot.active_window || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">File size</dt>
                  <dd className="text-foreground">
                    {(selectedScreenshot.file_size / 1024).toFixed(2)} KB
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Risk score</dt>
                  <dd>
                    <Badge variant={getRiskVariant(selectedScreenshot.risk_score)}>
                      {selectedScreenshot.risk_score}
                    </Badge>
                  </dd>
                </div>
                {selectedScreenshot.pii_types && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">PII types</dt>
                    <dd className="text-foreground">{selectedScreenshot.pii_types}</dd>
                  </div>
                )}
              </dl>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={closeDetails}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default ScreenshotsPage;
